import { Component, HostListener, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MdDialog, MdDialogRef, MD_DIALOG_DATA } from '@angular/material';
import { DialogGeneralMessageComponent } from 'app/components/modal/dialog-general-message/dialog-general-message.component';
import { CallingCodeOption } from 'app/model/constant';
import { Option } from 'app/model/dropdown/dropdown-option-model';
import { UserInfoModel } from 'app/model/profile/profile.model';
import { TranslatePipe } from 'app/pipes/translate.pipe';
import { UserFeatureService } from 'app/services/user-feature/user-feature.service';
import { Subscription } from 'rxjs';
import { UserService } from './../../../services/user/user.service';
import { DialogMyProfileForm as FormProvider } from './dialog-my-profile.form';
import { DialogMyProfileActionType, ProfileAdaptor, TimeOfBirthType } from './dialog-my-profile.model';
import { PhoneNumberValidator } from 'app/utilities/phone-number-validator';
import { CallingCode } from 'app/model/user/calling-code-model';
import { PhoneNumberUtil } from 'google-libphonenumber';
import * as moment from 'moment';

@Component({
  selector: 'act-dialog-my-profile',
  templateUrl: './dialog-my-profile.component.html',
  styleUrls: ['./dialog-my-profile.component.scss']
})
export class DialogMyProfileComponent implements OnInit, OnDestroy {
  public pageElementName = 'dialog-my-profile';
  public isShowEditAddressList;
  public phoneNumberMask = Array.from({ length: 20 }, () => /\d/);

  public options = { page: this.pageElementName, selectable: false, editable: true };
  public isLoading = false;
  public isSubmit = false;

  public form: FormGroup;
  public profile: ProfileAdaptor;
  public profileData: UserInfoModel;
  private currentState;
  public dates: Array<Number> = [];
  public months = [];
  public years: Array<Number> = [];
  public title: string;
  public subTitle: string;
  public submitText: string;
  public TimeOfBirthType = TimeOfBirthType;
  public timeOfBirths = [];

  private profileEditable = true;
  private isCallbackEditFromParent: boolean = false;

  private getCallingCodeSubscription: Subscription;

  public callingCodeList: Option[] = [CallingCodeOption.DEFAULT];

  private phoneNumberUtil = PhoneNumberUtil.getInstance();

  private readonly STATE = {
    VIEW: 'VIEW',
    EDIT: 'EDIT'
  };

  private readonly response = {
    SUCCESS: 'SUCCESS',
    FAIL: 'FAIL'
  };

  private STATUS = {
    INITIAL: 'INITIAL',
    PENDING: 'PENDING',
    RESPONSE: 'RESPONSE',
  };

  private dataStatus = {
    isInitial: false,
    isPending: false,
    isResponse: false,
  };

  constructor(
    public dialogRef: MdDialogRef<DialogMyProfileComponent>,
    @Inject(MD_DIALOG_DATA) public data:
      { profileData?: UserInfoModel, title: string, subTitle: string, submitText: string, applyMuTravel: boolean },
    private userService: UserService,
    private userFeatureService: UserFeatureService,
    private formBuilder: FormBuilder,
    private dialog: MdDialog,
    private translatePipe: TranslatePipe,
    private formProvider: FormProvider) {

    if (this.data.profileData) {
      this.profile = this.profileAdaptor(this.data.profileData);
      this.profileData = this.data.profileData;
    }
    this.buildForm();
    this.title = this.data.title;
    this.subTitle = this.data.subTitle;
    this.submitText = this.data.submitText;

    this.dates = this.createDates(31);
    this.months = this.createMonths();
    this.years = this.createYearsForAges({ after: 0, before: 120 });
    this.timeOfBirths = this.createTimeOfBirths();
  }

  @HostListener('window:resize', ['$event'])
  onResize(event?) {
    document.documentElement.style.setProperty('--height-vh', `${window.innerHeight * 0.01}px`);
  }

  ngOnInit() {
    this.onResize();
    this.currentState = this.STATE.VIEW;
    this.profileEditable = this.userFeatureService.canEditProfile();

    this.getCallingCodeSubscription && this.getCallingCodeSubscription.unsubscribe();
    this.getCallingCodeSubscription = this.userService.getCallingCode().subscribe(countryCallingCodeList => {
      if (countryCallingCodeList.length > 0) {
        this.callingCodeList = this.transformCallingCode(countryCallingCodeList);
      }
    });
  }

  handleCancel() {
    this.dialogRef.close({
      action: DialogMyProfileActionType.CANCEL
    });
  }

  isInvalid(field) {
    return this.form.get(field).invalid;
  }

  hasError(field, error) {
    return this.form.get(field).hasError(error);
  }

  touched(field) {
    return this.form.get(field).touched;
  }

  profileAdaptor(profileData: UserInfoModel): ProfileAdaptor {
    const { companyUser: { companyTitle, position }, telephone, countryCallingCode, birthDate = '', fullName, email, timeOfBirth } = profileData;

    const [year = '', month = '', date = ''] = !birthDate ? [] : String(birthDate).split('-');
    const callingCode = countryCallingCode ? countryCallingCode : `+${this.phoneNumberUtil.getCountryCodeForRegion(this.callingCodeList[0].value)}`;

    const countryISO2 = telephone ?
      this.phoneNumberUtil.getRegionCodeForNumber(this.phoneNumberUtil.parse(`${callingCode}${telephone}`))
      : this.phoneNumberUtil.getRegionCodeForCountryCode(+callingCode);

    return {
      birthDate,
      fullName,
      email,
      position,
      year: year ? parseInt(year, 10) + '' : '',
      month: month ? parseInt(month, 10) + '' : '',
      date: date ? parseInt(date, 10) + '' : '',
      companyName: companyTitle,
      contactNumber: telephone,
      countryCallingCode: callingCode,
      countryISO2: countryISO2,
      timeOfBirth: timeOfBirth
    };
  }

  createDates(last) {
    let current = 1;
    let dates = [];
    while (current <= last) {
      dates = [...dates, current];
      current = current + 1;
    }

    return dates;
  }

  updateDates(last) {
    this.dates = this.createDates(last);
  }

  createYearsForAges(period) {
    const current = (new Date()).getFullYear();
    let years = [];
    let startYear = current - period.after;
    const lastYear = current - period.before;
    while (startYear >= lastYear) {
      years = [...years, startYear];
      startYear = startYear - 1;
    }
    return years;
  }

  buildForm() {
    const { getInitForm } = this.formProvider;
    const { fullName, contactNumber, countryCallingCode, email, companyName, position, year, month, date, countryISO2, timeOfBirth } = this.profile;
    const _date = date ? parseInt(date + '', 10) === 0 ? '' : parseInt(date + '', 10) : '';
    const _month = month ? parseInt(month + '', 10) === 0 ? '' : parseInt(month + '', 10) : '';
    const _year = year ? parseInt(year + '', 10) === 0 ? '' : parseInt(year + '', 10) : '';
    const _timeOfBirth = timeOfBirth ? timeOfBirth : '';

    this.form = this.formBuilder.group(getInitForm(
      fullName,
      companyName,
      position,
      _date,
      _month,
      _year,
      _timeOfBirth
    ));

    if (this.profileData.birthDate) {
      this.form.controls['date'].disable();
      this.form.controls['month'].disable();
      this.form.controls['year'].disable();
    }
    if (this.profileData.timeOfBirth) {
      this.form.controls['timeOfBirth'].disable();
    }
  }

  get isEditForm() {
    return this.currentState === this.STATE.EDIT;
  }

  get isViewForm() {
    return this.currentState === this.STATE.VIEW;
  }

  setLoadingState(isLoading: boolean) {
    this.isLoading = isLoading;
  }

  onSubmit(form: FormGroup) {
    this.isSubmit = true;
    this.setLoadingState(true);

    if (form.invalid || this.isInvalidBirthDate()) {
      this.setLoadingState(false);
      return;
    }

    const { companyId, userId } = this.userService.getUser();
    const { fullName, contactNumber, position, timeOfBirth } = form.getRawValue();
    let birthDate = this.getBirthDate(form)
    const payload = {
      fullName: fullName,
      companyId: companyId,
      userId: userId,
      birthDate: birthDate,
      position: position,
      timeOfBirth: timeOfBirth
    }
    this.userService.editProfile(payload).subscribe((res) => {
      this.handleOpenDialog({ type: 'SUCCESS', ...{ fullName, contactNumber, birthDate, timeOfBirth } });
    }, (eror) => {
      this.handleOpenDialog({ type: 'FAILURE', ...{ fullName, contactNumber, birthDate, timeOfBirth } });
    });
  }

  getBirthDate(form: FormGroup) {
    const { date, month, year } = form.getRawValue();
    let _month = month < 10 ? '0' + month : month;
    let _date = date < 10 ? '0' + date : date;
    let _year = year == 0 ? '0000' : year;
    let birthDate = [_year, _month, _date].join('-');
    if (!date || !month || !year) {
      birthDate = null;
    }
    return birthDate;
  }
  translate(value) {
    return this.translatePipe.transform(value);
  }

  createMessage({ type }) {
    if (type === this.response.SUCCESS) {
      return {
        icon: 'icon-successful',
        title: this.translate('my-profile-edit-success-title'),
        message: this.translate('my-profile-edit-success-description'),
        mainBtn: this.translate('btn-ok')
      };
    } else {
      return {
        icon: 'icon-service-failed',
        title: this.translate('my-profile-edit-failure-title'),
        message: this.translate('my-profile-edit-failure-description'),
        mainBtn: this.translate('btn-ok')
      };
    }
  }

  handleOpenDialog = ({ type, fullName, contactNumber, birthDate, timeOfBirth }) => {
    this.setLoadingState(false);

    if (type === this.response.SUCCESS) {
      const { year, month, date } = this.form.getRawValue();
      this.profile = { ...this.form.getRawValue(), birthDate };
      if (!birthDate) {
        this.profile = { ...this.profile, date: '', month: '', year: '' };
      }
      this.userService.updateUser({
        fullName: fullName,
        telephone: contactNumber,
        birthDate: birthDate,
        timeOfBirth: timeOfBirth
      });
    }

    if (!this.data.applyMuTravel) {
      const message = this.createMessage({ type });

      const config = {
        hasBackdrop: true,
        disableClose: false,
        focusOnOpen: false,
        data: message
      };

      this.dialog.open(DialogGeneralMessageComponent, config);
    }
    this.dialogRef.close({ action: DialogMyProfileActionType.UPDATED })
  };

  onClickEditProfile() {
    this.dialog.open(DialogMyProfileComponent)
  }

  onSelectCallingCode(event) {
    this.form.controls.contactNumber.setValidators([Validators.required, PhoneNumberValidator(event)]);
    this.form.controls.contactNumber.updateValueAndValidity();
  }

  transformCallingCode(callingCodeList: CallingCode[]): Option[] {
    const callingCodeOptionList = [];
    callingCodeList.forEach(item => {
      if (CallingCodeOption.DEFAULT.value === item.countryISO2) {
        callingCodeOptionList.unshift(new Option(
          item.countryNameEN + ' (' + item.countryCallingCode + ')',
          item.countryISO2,
          item.countryISO2 ? 'flags/' + item.countryISO2.toLowerCase() + '.png' : '',
          item.countryCallingCode
        ));
      } else {
        callingCodeOptionList.push(new Option(
          item.countryNameEN + ' (' + item.countryCallingCode + ')',
          item.countryISO2,
          item.countryISO2 ? 'flags/' + item.countryISO2.toLowerCase() + '.png' : '',
          item.countryCallingCode
        ));
      }
    });
    return callingCodeOptionList;
  }

  ngOnDestroy(): void {
    this.getCallingCodeSubscription && this.getCallingCodeSubscription.unsubscribe();
  }

  removeSpaceBetween(event, name: string) {
    this.form.controls[name].patchValue(event.target.value.replace(/\s+/g, ' ').trim());
    this.form.controls[name].updateValueAndValidity();
  }

  removeSpace(event, name: string) {
    this.form.controls[name].patchValue(event.target.value.trim());
    this.form.controls[name].updateValueAndValidity();
  }

  isInvalidBirthDate() {
    if (this.isInvalid('date') || this.isInvalid('month') || this.isInvalid('year')) {
      return true;
    }

    const date = this.form.get('date').value;
    const month = this.form.get('month').value;
    const year = this.form.get('year').value;
    if (!moment(`${year}-${month}-${date}`, ['YYYY-MM-DD', 'YYYY-M-D'], true).isValid()) {
      return true;
    }

    return false;
  }

  createMonths() {
    return [
      {
        value: '1',
        label: 'month-january'
      },
      {
        value: '2',
        label: 'month-february'
      },
      {
        value: '3',
        label: 'month-march'
      },
      {
        value: '4',
        label: 'month-april'
      },
      {
        value: '5',
        label: 'month-may'
      },
      {
        value: '6',
        label: 'month-june'
      },
      {
        value: '7',
        label: 'month-july'
      },
      {
        value: '8',
        label: 'month-august'
      },
      {
        value: '9',
        label: 'month-september'
      },
      {
        value: '10',
        label: 'month-october'
      },
      {
        value: '11',
        label: 'month-november'
      },
      {
        value: '12',
        label: 'month-december'
      },
    ];
  }

  createTimeOfBirths() {
    return [
      {
        value: TimeOfBirthType.UN_SPECIFIED,
        label: 'time-of-birth-option-unspecified'
      },
      {
        value: TimeOfBirthType.DAY,
        label: 'time-of-birth-option-day'
      },
      {
        value: TimeOfBirthType.NIGHT,
        label: 'time-of-birth-option-night'
      }
    ];
  }

}



// WEBPACK FOOTER //
// ./src/app/pages/my-profile/dialog-my-profile/dialog-my-profile.component.ts